import { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { X, Check, ChevronRight, ChevronLeft, Sparkles } from 'lucide-react';
import { TEMPLATE_CATEGORIES, getTemplatesByCategory, type Template } from '@/lib/templates';
import { FEATURE_CATEGORIES, getFeaturesByCategory, getDefaultEnabledFeatures, type FeatureCategory } from '@/lib/features';
import { TemplatePreview } from './TemplatePreview';
import { TemplateFullPreview } from './TemplateFullPreview';

interface SiteCreationModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (templateId: string, selectedFeatures: string[], selectedLayouts: string[]) => void;
  businessName: string;
  loading?: boolean;
}

type Step = 'template' | 'features';

export function SiteCreationModal({ isOpen, onClose, onSubmit, businessName, loading }: SiteCreationModalProps) {
  const [step, setStep] = useState<Step>('template');
  const [selectedCategory, setSelectedCategory] = useState<string>(TEMPLATE_CATEGORIES[0]);
  const [selectedTemplate, setSelectedTemplate] = useState<Template | null>(null);
  const [selectedFeatures, setSelectedFeatures] = useState<string[]>(getDefaultEnabledFeatures());
  const [featureCategory, setFeatureCategory] = useState<FeatureCategory>('essential');
  const [previewTemplate, setPreviewTemplate] = useState<Template | null>(null);

  useEffect(() => {
    if (isOpen) {
      setStep('template');
      setSelectedCategory(TEMPLATE_CATEGORIES[0]);
      setSelectedTemplate(null);
      setSelectedFeatures(getDefaultEnabledFeatures());
      setFeatureCategory('essential');
      setPreviewTemplate(null);
      // Lock body scroll when modal is open
      document.body.style.overflow = 'hidden';
      return () => { document.body.style.overflow = ''; };
    }
  }, [isOpen]);

  if (!isOpen) return null;

  const categoryTemplates = getTemplatesByCategory(selectedCategory);
  const categoryFeatures = getFeaturesByCategory(featureCategory);

  const toggleFeature = (featureId: string) => {
    setSelectedFeatures((prev) =>
      prev.includes(featureId)
        ? prev.filter((id) => id !== featureId)
        : [...prev, featureId]
    );
  };

  const handleSubmit = () => {
    if (selectedTemplate) {
      onSubmit(selectedTemplate.id, selectedFeatures, []);
    }
  };

  return createPortal(
    <div
      className="animate-modal-backdrop"
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 9999,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '16px',
        background: 'rgba(0,0,0,0.9)',
        backdropFilter: 'blur(12px)',
      }}
      onClick={(e) => e.target === e.currentTarget && onClose()}
    >
      <div
        className="animate-modal-in"
        style={{
          width: '100%',
          maxWidth: '1000px',
          maxHeight: '90vh',
          background: 'rgba(0,0,0,0.98)',
          border: '1px solid rgba(91,58,58,0.15)',
          borderRadius: '16px',
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
          boxShadow: '0 25px 50px -12px rgba(0,0,0,0.5), 0 0 0 1px rgba(91,58,58,0.1)',
        }}
      >
        {/* Header */}
        <div
          style={{
            padding: '20px 24px',
            borderBottom: '1px solid rgba(91,58,58,0.15)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            flexShrink: 0,
          }}
        >
          <div>
            <h2 style={{ fontSize: '20px', fontWeight: 600, color: '#FFFBFE', marginBottom: '4px' }}>
              Create Site for {businessName}
            </h2>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <span
                style={{
                  padding: '4px 12px',
                  borderRadius: '9999px',
                  fontSize: '12px',
                  fontWeight: 500,
                  background: step === 'template' ? 'linear-gradient(to right, #B08080, #B08080)' : 'rgba(255,255,255,0.1)',
                  color: step === 'template' ? 'white' : '#888',
                }}
              >
                1. Template
              </span>
              <ChevronRight style={{ width: '16px', height: '16px', color: '#666' }} />
              <span
                style={{
                  padding: '4px 12px',
                  borderRadius: '9999px',
                  fontSize: '12px',
                  fontWeight: 500,
                  background: step === 'features' ? 'linear-gradient(to right, #B08080, #B08080)' : 'rgba(255,255,255,0.1)',
                  color: step === 'features' ? 'white' : '#888',
                }}
              >
                2. Features
              </span>
            </div>
          </div>
          <button
            onClick={onClose}
            style={{
              padding: '8px',
              borderRadius: '8px',
              background: 'rgba(91,58,58,0.1)',
              border: '1px solid rgba(91,58,58,0.2)',
              cursor: 'pointer',
              color: '#B08080',
            }}
          >
            <X style={{ width: '20px', height: '20px' }} />
          </button>
        </div>

        {/* Content */}
        <div style={{ flex: 1, overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
          {step === 'template' ? (
            <>
              {/* Category Tabs */}
              <div
                style={{
                  padding: '16px 24px',
                  borderBottom: '1px solid rgba(91,58,58,0.1)',
                  display: 'flex',
                  gap: '8px',
                  overflowX: 'auto',
                  flexShrink: 0,
                }}
              >
                {TEMPLATE_CATEGORIES.map((cat) => (
                  <button
                    key={cat}
                    onClick={() => setSelectedCategory(cat)}
                    style={{
                      padding: '8px 16px',
                      borderRadius: '8px',
                      border: 'none',
                      cursor: 'pointer',
                      fontSize: '14px',
                      fontWeight: 500,
                      whiteSpace: 'nowrap',
                      background: selectedCategory === cat ? 'rgba(91, 58, 58, 0.2)' : 'transparent',
                      color: selectedCategory === cat ? '#B08080' : '#888',
                      transition: 'all 0.2s',
                    }}
                  >
                    {cat}
                  </button>
                ))}
              </div>

              {/* Templates Grid */}
              <div style={{ flex: 1, overflow: 'auto', padding: '24px' }}>
                <div
                  style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))',
                    gap: '20px',
                  }}
                >
                  {categoryTemplates.map((template) => (
                    <TemplatePreview
                      key={template.id}
                      template={template}
                      selected={selectedTemplate?.id === template.id}
                      onClick={() => setSelectedTemplate(template)}
                      onPreview={() => setPreviewTemplate(template)}
                      businessName={businessName}
                    />
                  ))}
                </div>
              </div>
            </>
          ) : (
            <>
              {/* Feature Category Tabs */}
              <div
                style={{
                  padding: '16px 24px',
                  borderBottom: '1px solid rgba(91,58,58,0.1)',
                  display: 'flex',
                  gap: '8px',
                  overflowX: 'auto',
                  flexShrink: 0,
                }}
              >
                {FEATURE_CATEGORIES.map((cat) => (
                  <button
                    key={cat.id}
                    onClick={() => setFeatureCategory(cat.id)}
                    style={{
                      padding: '8px 16px',
                      borderRadius: '8px',
                      border: 'none',
                      cursor: 'pointer',
                      fontSize: '14px',
                      fontWeight: 500,
                      whiteSpace: 'nowrap',
                      background: featureCategory === cat.id ? 'rgba(91, 58, 58, 0.2)' : 'transparent',
                      color: featureCategory === cat.id ? '#B08080' : '#888',
                      transition: 'all 0.2s',
                    }}
                  >
                    {cat.name}
                  </button>
                ))}
              </div>

              {/* Features List */}
              <div style={{ flex: 1, overflow: 'auto', padding: '24px' }}>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                  {categoryFeatures.map((feature) => (
                    <button
                      key={feature.id}
                      onClick={() => toggleFeature(feature.id)}
                      style={{
                        padding: '16px',
                        border: selectedFeatures.includes(feature.id)
                          ? '1px solid rgba(91, 58, 58, 0.5)'
                          : '1px solid rgba(91,58,58,0.1)',
                        borderRadius: '12px',
                        cursor: 'pointer',
                        background: selectedFeatures.includes(feature.id)
                          ? 'rgba(91, 58, 58, 0.1)'
                          : 'rgba(0,0,0,0.5)',
                        textAlign: 'left',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '16px',
                        transition: 'all 0.2s',
                      }}
                    >
                      <div
                        style={{
                          width: '24px',
                          height: '24px',
                          borderRadius: '6px',
                          border: selectedFeatures.includes(feature.id)
                            ? '2px solid #B08080'
                            : '2px solid rgba(255,255,255,0.2)',
                          background: selectedFeatures.includes(feature.id) ? '#B08080' : 'transparent',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          flexShrink: 0,
                        }}
                      >
                        {selectedFeatures.includes(feature.id) && (
                          <Check style={{ width: '14px', height: '14px', color: '#FFFBFE' }} />
                        )}
                      </div>
                      <div style={{ flex: 1 }}>
                        <p style={{ fontSize: '15px', fontWeight: 500, color: '#FFFBFE' }}>
                          {feature.name}
                        </p>
                        <p style={{ fontSize: '13px', color: '#888888', marginTop: '2px' }}>
                          {feature.description}
                        </p>
                      </div>
                    </button>
                  ))}
                </div>
              </div>
            </>
          )}
        </div>

        {/* Footer */}
        <div
          style={{
            padding: '20px 24px',
            borderTop: '1px solid rgba(91,58,58,0.15)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            flexShrink: 0,
          }}
        >
          <div style={{ color: '#888888', fontSize: '14px' }}>
            {step === 'template' ? (
              selectedTemplate ? (
                <span>Selected: <strong style={{ color: '#FFFBFE' }}>{selectedTemplate.name}</strong></span>
              ) : (
                'Select a template to continue'
              )
            ) : (
              <span><strong style={{ color: '#FFFBFE' }}>{selectedFeatures.length}</strong> features selected</span>
            )}
          </div>
          <div style={{ display: 'flex', gap: '12px' }}>
            {step === 'features' && (
              <button
                onClick={() => setStep('template')}
                style={{
                  padding: '10px 20px',
                  borderRadius: '8px',
                  border: '1px solid rgba(91,58,58,0.3)',
                  background: 'rgba(91,58,58,0.1)',
                  color: '#B08080',
                  cursor: 'pointer',
                  fontSize: '14px',
                  fontWeight: 500,
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px',
                }}
              >
                <ChevronLeft style={{ width: '16px', height: '16px' }} />
                Back
              </button>
            )}
            {step === 'template' ? (
              <button
                onClick={() => setStep('features')}
                disabled={!selectedTemplate}
                style={{
                  padding: '10px 20px',
                  borderRadius: '8px',
                  border: 'none',
                  background: selectedTemplate
                    ? 'linear-gradient(to right, #B08080, #B08080)'
                    : 'rgba(91,58,58,0.15)',
                  color: selectedTemplate ? 'white' : '#666',
                  cursor: selectedTemplate ? 'pointer' : 'not-allowed',
                  fontSize: '14px',
                  fontWeight: 500,
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px',
                }}
              >
                Next
                <ChevronRight style={{ width: '16px', height: '16px' }} />
              </button>
            ) : (
              <button
                onClick={handleSubmit}
                disabled={loading}
                style={{
                  padding: '10px 24px',
                  borderRadius: '8px',
                  border: 'none',
                  background: 'linear-gradient(to right, #B08080, #B08080)',
                  color: '#FFFBFE',
                  cursor: loading ? 'wait' : 'pointer',
                  fontSize: '14px',
                  fontWeight: 500,
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  opacity: loading ? 0.7 : 1,
                }}
              >
                {loading ? (
                  <>
                    <div
                      style={{
                        width: '16px',
                        height: '16px',
                        border: '2px solid rgba(255,255,255,0.3)',
                        borderTopColor: 'white',
                        borderRadius: '50%',
                        animation: 'spin 1s linear infinite',
                      }}
                    />
                    Generating...
                  </>
                ) : (
                  <>
                    <Sparkles style={{ width: '16px', height: '16px' }} />
                    Generate Site
                  </>
                )}
              </button>
            )}
          </div>
        </div>

        <style>{`
          @keyframes spin {
            to { transform: rotate(360deg); }
          }
        `}</style>
      </div>
      {previewTemplate && (
        <TemplateFullPreview
          template={previewTemplate}
          businessName={businessName}
          selectedFeatures={selectedFeatures}
          onClose={() => setPreviewTemplate(null)}
        />
      )}
    </div>,
    document.body
  );
}
